import React, { useState, useRef, useEffect } from 'react';

interface ShortcutButtonProps {
  onCreateNote?: () => void;
  onCreateGoal?: () => void;
  className?: string;
}

// SVGアイコンコンポーネント
const PlusIcon = () => (
  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CloseIcon = () => (
  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const FileTextIcon = () => (
  <svg
    className="w-6 h-6"
    aria-hidden="true"
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    fill="none"
    viewBox="0 0 24 24">
    <path
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M10 3v4a1 1 0 0 1-1 1H5m4 8h6m-6-4h6m4-8v16a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V7.914a1 1 0 0 1 .293-.707l3.914-3.914A1 1 0 0 1 9.914 3H18a1 1 0 0 1 1 1Z"
    />
  </svg>
);

const TargetIcon = () => (
  <svg
    className="w-6 h-6"
    aria-hidden="true"
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    fill="none"
    viewBox="0 0 24 24">
    <path
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M15 4h3a1 1 0 0 1 1 1v15a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h3m0 3h6m-6 5h6m-6 4h6M10 3v4h4V3h-4Z"
    />
  </svg>
);

export const ShortcutButton: React.FC<ShortcutButtonProps> = ({
  onCreateNote = () => console.log('ノート新規作成'),
  onCreateGoal = () => console.log('目標新規作成'),
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // 外側クリックでメニューを閉じる
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleMainButtonClick = () => {
    setIsOpen(!isOpen);
  };

  const handleOptionClick = (action: () => void) => {
    action();
    setIsOpen(false);
  };

  // 扇形の角度計算（左上方向に展開）
  const getButtonPosition = (index: number) => {
    const baseAngle = 200; // 左上方向を基準（225度）
    const angleSpread = 60; // 扇形の角度幅
    const angle = baseAngle + index * angleSpread;
    const distance = 80; // メインボタンからの距離

    const radian = (angle * Math.PI) / 180;
    const x = Math.cos(radian) * distance;
    const y = Math.sin(radian) * distance;

    return { x, y };
  };

  const options = [
    {
      icon: <FileTextIcon />,
      label: '新しいノート',
      color: 'bg-green-500 hover:bg-green-400',
      iconColor: 'text-white',
      action: onCreateNote
    },
    {
      icon: <TargetIcon />,
      label: '新しい目標',
      color: 'bg-green-500 hover:bg-green-400',
      iconColor: 'text-white',
      action: onCreateGoal
    }
  ];

  return (
    <div ref={containerRef} className={`${className}`}>
      {/* 扇形に展開する選択肢ボタン */}
      {options.map((option, index) => {
        const position = getButtonPosition(index);

        return (
          <div
            key={index}
            className={`absolute transition-all duration-500 ease-out ${
              isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
            }`}
            style={{
              transform: isOpen ? `translate(${position.x}px, ${position.y}px)` : 'translate(0, 0)',
              transitionDelay: isOpen ? `${index * 100}ms` : '0ms'
            }}>
            {/* ラベル */}
            <div
              className={`absolute right-14 top-1/2 transform -translate-y-1/2 bg-gray-800 text-white px-3 py-1 rounded-lg text-sm whitespace-nowrap transition-all duration-300 ${
                isOpen ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-2'
              }`}
              style={{
                transitionDelay: isOpen ? `${index * 100 + 200}ms` : '0ms'
              }}>
              {option.label}
              {/* 矢印 */}
              {/* <div className="absolute right-0 top-1/2 transform translate-x-1 -translate-y-1/2 w-0 h-0 border-l-4 border-l-gray-800 border-t-4 border-t-transparent border-b-4 border-b-transparent"></div> */}
            </div>

            {/* 選択肢ボタン */}
            <button
              onClick={() => handleOptionClick(option.action)}
              className={`w-12 h-12 ${option.color} rounded-full shadow-lg flex items-center justify-center transition-all duration-200 hover:scale-110 transform`}>
              <div className={option.iconColor}>{option.icon}</div>
            </button>
          </div>
        );
      })}

      {/* 接続線（視覚的効果） */}
      {/* {isOpen && (
        <svg
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 pointer-events-none"
          width="200"
          height="200"
          style={{ zIndex: -1 }}>
          {options.map((_, index) => {
            const position = getButtonPosition(index);
            return (
              <line
                key={index}
                x1="100"
                y1="100"
                x2={100 + position.x}
                y2={100 + position.y}
                stroke="rgba(59, 130, 246, 0.2)"
                strokeWidth="2"
                strokeDasharray="4,4"
                className="animate-pulse"
              />
            );
          })}
        </svg>
      )} */}

      {/* メインのショートカットボタン */}
      <button
        onClick={handleMainButtonClick}
        className={`relative w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110 ${
          isOpen ? 'rotate-135 bg-green-400' : 'bg-green-500 rotate-0'
        }`}>
        <div className="text-white">{isOpen ? <CloseIcon /> : <PlusIcon />}</div>

        {/* リップルエフェクト */}
        <div
          className={`absolute inset-0 rounded-full ${
            isOpen ? 'animate-ping bg-green-400' : 'animate-none'
          } opacity-20`}></div>
      </button>
    </div>
  );
};

export default ShortcutButton;
